import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { AlertCircle, ArrowRight } from 'lucide-react';
import { fetchMyHostListings } from '../../utils/hostListings';

/**
 * Per-listing banner shown to hosts who have at least one approved listing whose
 * Airbnb settings are NOT yet matched in-app. Until a listing is matched, it
 * won't emit iCal reservation signals and can't generate manual invites. Other
 * matched listings keep working normally.
 *
 * Tapping the banner deep-links to that listing's sync settings.
 */
export function HostAirbnbSettingsBanner({ user, style }) {
  const navigate = useNavigate();
  const [unmatched, setUnmatched] = useState([]);

  const isHost = user?.role === 'host';

  useEffect(() => {
    let active = true;
    if (!isHost) {
      setUnmatched([]);
      return undefined;
    }
    (async () => {
      try {
        const listings = await fetchMyHostListings();
        if (!active) return;
        setUnmatched(listings.filter((listing) => listing.isActiveForInvites && !listing.airbnbSettingsCompleted));
      } catch (_err) {
        if (active) setUnmatched([]);
      }
    })();
    return () => {
      active = false;
    };
  }, [isHost]);

  if (!isHost || !unmatched.length) return null;

  const single = unmatched.length === 1;
  const title = single
    ? `Match ${unmatched[0].name}'s Airbnb settings`
    : `${unmatched.length} listings need their Airbnb settings matched`;
  const subtitle = single
    ? "Until you do, it won't sync iCal reservations or allow manual invites."
    : "Until you match each one, those listings won't sync iCal reservations or allow manual invites.";

  return (
    <button
      type="button"
      data-testid="host-airbnb-settings-banner"
      onClick={() => navigate('/host/generate-qr', { state: { openSyncSettings: true, targetListingId: unmatched[0].id } })}
      style={{
        all: 'unset',
        cursor: 'pointer',
        display: 'flex',
        alignItems: 'center',
        gap: 12,
        padding: '12px 16px',
        margin: '12px 16px 4px',
        borderRadius: 16,
        background: 'var(--status-warning-surface)',
        border: '1px solid rgba(221,6,8,0.18)',
        boxShadow: '0 4px 14px rgba(221,6,8,0.06)',
        ...style,
      }}
    >
      <div
        aria-hidden="true"
        style={{
          width: 36,
          height: 36,
          borderRadius: 999,
          background: 'rgba(221,6,8,0.12)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          flexShrink: 0,
        }}
      >
        <AlertCircle size={18} color="#DD0608" />
      </div>
      <div style={{ flex: 1, minWidth: 0 }}>
        <p
          data-testid="host-airbnb-settings-banner-title"
          style={{
            margin: 0,
            fontFamily: "'Inria Serif', serif",
            fontSize: 14,
            fontWeight: 700,
            color: 'var(--text)',
            lineHeight: 1.2,
          }}
        >
          {title}
        </p>
        <p
          style={{
            margin: '4px 0 0',
            fontFamily: "'Inria Serif', serif",
            fontSize: 12,
            color: 'var(--text-2)',
            lineHeight: 1.3,
          }}
        >
          {subtitle}
        </p>
      </div>
      <ArrowRight size={18} color="#DD0608" aria-hidden="true" />
    </button>
  );
}
